import React from 'react';
import { formatInTimeZone, fromZonedTime } from 'date-fns-tz';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { timezoneToCountryCode, countryCodeToName } from '../../../utils/timezoneToCountry';
import { BA_TIMEZONE } from '../../../utils/timezone';

interface TimezoneAgendaUpdatedModalProps {
  open: boolean;
  /** Huso horario que tenía configurado la psicóloga ANTES del cambio. */
  prevTimezone: string | null;
  /** Huso horario que acaba de configurar. */
  newTimezone: string | null;
  onClose: () => void;
}

// Hora de referencia para el ejemplo cuando no hay una sesión agendada de la que tomar un
// horario real (no hay forma liviana/determinística de leer "la primera sesión agendada" en
// el instante en que este modal aparece, recién llegando a Agenda tras invalidar la caché de
// sesiones — se muestra antes de que WeeklyPsychologistCalendar vuelva a pedirlas).
const FALLBACK_REFERENCE_HOUR = 10;

/** "España", "Colombia", "Argentina"… — nombre de país a secas, sin ciudad ni código. */
function timezoneCountryLabel(tz: string): string {
  const code = timezoneToCountryCode(tz);
  const name = code ? countryCodeToName(code) : null;
  return name ?? tz;
}

/** Convierte una hora de referencia (hoy, HH:00) de `fromTz` a `toTz` → "HH:mm" en cada uno. */
function convertReferenceHour(fromTz: string, toTz: string): { fromLabel: string; toLabel: string } {
  const now = new Date();
  const base = new Date(now.getFullYear(), now.getMonth(), now.getDate(), FALLBACK_REFERENCE_HOUR, 0, 0);
  const utc = fromZonedTime(base, fromTz);
  return {
    fromLabel: formatInTimeZone(utc, fromTz, 'HH:mm'),
    toLabel: formatInTimeZone(utc, toTz, 'HH:mm'),
  };
}

interface InfoSection {
  icon: string;
  question: string;
  answer: string;
}

/**
 * Modal mostrado en Agenda inmediatamente después de que la psicóloga guarda
 * un cambio de huso horario en Perfil (que la redirige acá). Calca el patrón
 * visual de RescheduleInfoModal ("Política de horarios de reprogramación").
 */
const TimezoneAgendaUpdatedModal: React.FC<TimezoneAgendaUpdatedModalProps> = ({ open, prevTimezone, newTimezone, onClose }) => {
  const prevTz = prevTimezone || BA_TIMEZONE;
  const newTz = newTimezone || BA_TIMEZONE;
  const prevLabel = timezoneCountryLabel(prevTz);
  const newLabel = timezoneCountryLabel(newTz);
  const { fromLabel, toLabel } = convertReferenceHour(prevTz, newTz);

  const sections: InfoSection[] = [
    {
      icon: '🔒',
      question: '¿Afecta a mis sesiones o a mis pacientes?',
      answer:
        'No. Es un cambio solo visual: no afecta a tus sesiones agendadas ni a tus pacientes de ninguna manera, y no hace falta avisarles nada.',
    },
    {
      icon: '🕐',
      question: '¿Qué se tradujo?',
      answer:
        `Todo lo que estaba cargado en hora ${prevLabel} —tus horarios disponibles, bloqueos y sesiones— ahora se muestra en tu hora ${newLabel}. Es el mismo horario de siempre, solo que ya no tenés que convertirlo vos.`,
    },
    {
      icon: '💡',
      question: 'Ejemplo',
      answer:
        `Si tenías una sesión a las ${fromLabel} ${prevLabel}, ahora la vas a ver a las ${toLabel} (${newLabel}). Es la misma sesión, al mismo momento — antes la veías en ${prevLabel} y tenías que hacer la conversión, ahora ya está hecha.`,
    },
  ];

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent
        data-testid="timezone-agenda-updated-modal"
        className="sm:max-w-[30rem] p-0 overflow-hidden"
      >
        {/* Header */}
        <DialogHeader className="px-6 pt-6 pb-4 mb-0 border-b border-gray-100">
          <DialogTitle className="text-[1.0625rem] font-semibold text-[#4a3d73]">
            Actualizamos tu huso horario
          </DialogTitle>
          <p className="text-[0.8125rem] text-gray-500 mt-1 leading-snug">
            Esto es lo que cambia para vos.
          </p>
        </DialogHeader>

        {/* Body */}
        <div className="px-6 py-5 space-y-5 overflow-y-auto max-h-[60vh]">
          {sections.map((section) => (
            <InfoCard key={section.question} section={section} />
          ))}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 flex justify-end items-center gap-3">
          <button
            type="button"
            data-testid="timezone-agenda-updated-modal-cta"
            onClick={onClose}
            className="px-5 py-2 rounded-full text-sm font-semibold text-white border-none cursor-pointer transition-all hover:brightness-90"
            style={{ background: '#6D57BC' }}
          >
            Entendido
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const InfoCard: React.FC<{ section: InfoSection }> = ({ section }) => (
  <div className="flex gap-3 items-start">
    <span className="text-base leading-none mt-0.5 shrink-0" aria-hidden>
      {section.icon}
    </span>
    <div className="min-w-0">
      <p className="text-[0.8125rem] font-semibold text-[#4a3d73] leading-snug mb-1">
        {section.question}
      </p>
      <p className="text-[0.8125rem] text-gray-600 leading-relaxed">
        {section.answer}
      </p>
    </div>
  </div>
);

export default TimezoneAgendaUpdatedModal;
